import React, { useCallback } from 'react';
import { Select } from 'antd';
import styled from 'styled-components';

const PreviewContainer = styled.div`
  border-radius: 8px;
  padding: 20px;
  background: white;
  border: 1px solid #f0f0f0;
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const MappingColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.div`
  width: 120px;
  font-weight: 500;
`;

const SelectContainer = styled.div`
  width: 200px;
`;

const PreviewValues = styled.div`
  margin-left: 20px;
  color: #666;
  flex: 1;
`;

/**
 * Component to display a preview of spreadsheet data in an Excel-like grid format
 * @param {Object} props Component props
 * @param {Array} props.data Array of objects containing row data
 * @param {Object} props.mappings Mapping object containing column mappings
 * @param {Object} props.settings Global settings object
 * @param {Array} props.requiredMappings Array of required mappings
 * @param {Function} props.onMappingChange Callback function when mapping changes
 * @returns {JSX.Element?} SheetPreview component
 */
const SheetPreview = ({
  data = [],
  mappings = {},
  settings = {},
  requiredMappings = [],
  onMappingChange,
  disabled = false,
}) => {
  const allPossibleColummns = useCallback(() => {
    if (data?.length > 0) {
      const allPossibleColummns = new Set();
      data.forEach(row => {
        Object.keys(row).forEach(key => {
          allPossibleColummns.add(key);
        });
      });
      return Array.from(allPossibleColummns).sort((a, b) =>
        a.length !== b.length ? a.length - b.length : a.localeCompare(b)
      );
    }
  }, [data]);

  if (!data || data.length === 0) {
    return null;
  }

  /**
   * Generates column options for the select dropdown
   * @returns {Array<{label: string, value: number}>} Array of column options with labels and values
   */
  const getColumnOptions = () => {
    return allPossibleColummns().map(key => {
      return {
        label: settings.includeHeaders
          ? `Column ${key}`
          : `${data[0][key] || ''} (Column ${key})`,
        value: key,
      };
    });
  };

  /**
   * Generates preview values for the selected column
   * @param {string} column - Letter of the selected column
   * @returns {string} Comma-separated preview values
   */
  const getPreviewValues = column => {
    if (!column) return '';
    const start = settings.includeHeaders ? 0 : 1;
    const values = data.slice(start, start + 3).map(row => row[column]);

    return values.join(', ');
  };

  return (
    <PreviewContainer>
      {requiredMappings.map(mapping => (
        <MappingRow key={mapping.id}>
          <Label>{mapping.label}:</Label>
          <MappingColumn>
            <SelectContainer>
              <Select
                style={{ width: '100%' }}
                value={mappings[mapping.id]?.column}
                onChange={value => onMappingChange(mapping.id, value)}
                options={getColumnOptions()}
                placeholder='Select a column'
                disabled={disabled}
              />
            </SelectContainer>
          </MappingColumn>
          {mappings[mapping.id]?.column != null && (
            <PreviewValues>
              Preview: {getPreviewValues(mappings[mapping.id]?.column)}
            </PreviewValues>
          )}
        </MappingRow>
      ))}
    </PreviewContainer>
  );
};

export default SheetPreview;
